import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepButton,
  Chip,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@tanstack/react-query";
import CustomerService from "../../../services/Customer";
import {
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { defaultStorefronts, personas as personaOptions } from "./test-data";

const steps = ["Details", "Permissions", "Customers"];

const AddUserDialog = ({ open, onClose, onSubmit }) => {
  const isControlled = typeof open === "boolean";
  const [openInternal, setOpenInternal] = useState(false);
  const dialogOpen = isControlled ? open : openInternal;
  const [activeStep, setActiveStep] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [storefront, setStorefront] = useState(
    defaultStorefronts[0]?.value || ""
  );
  const [persona, setPersona] = useState("");
  const [customers, setCustomers] = useState([]);
  const [touched, setTouched] = useState({});

  const { data: customerOptions = [], isLoading: loadingCustomers } = useQuery({
    queryKey: ["customerList"],
    queryFn: CustomerService.fetchCustomers,
    enabled: open,
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  const isValidEmail = (val) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(val);

  const detailsValid = useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isValidEmail(email.trim()) &&
      storefront &&
      (secondaryEmail.trim().length === 0 ||
        isValidEmail(secondaryEmail.trim()))
    );
  }, [firstName, lastName, email, secondaryEmail, storefront]);

  const permissionsValid = useMemo(() => !!persona, [persona]);

  const canFinish = detailsValid && permissionsValid && customers.length > 0;

  const reset = () => {
    setActiveStep(0);
    setFirstName("");
    setLastName("");
    setEmail("");
    setSecondaryEmail("");
    setStorefront(defaultStorefronts[0]?.value || "");
    setPersona("");
    setCustomers([]);
    setTouched({});
    setIsSuccess(false);
    setCreatedUser(null);
  };

  const handleClose = () => {
    reset();
    onClose && onClose();
    if (!isControlled) setOpenInternal(false);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        secondaryEmail: true,
        storefront: true,
      });

      if (!detailsValid) return;

      setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      setTouched((t) => ({ ...t, persona: true }));

      if (!permissionsValid) return;

      setActiveStep(2);
      return;
    }
  };

  const handleCreate = async () => {
    if (!canFinish) return;

    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      secondaryEmail: secondaryEmail.trim() || "",
      persona,
      storefront,
      customers: customers.map((c) => ({
        id: c.id ?? c.externalId,
        externalId: c.externalId,
        name: c.name,
      })),
    };

    let result = null;
    if (onSubmit) {
      try {
        result = await Promise.resolve(onSubmit(payload));
      } catch (_err) {
        // ignore in UI-only flow
      }
    }
    setCreatedUser(result || payload);
    setIsSuccess(true);
  };

  const DIALOG_HEIGHT = 680;

  return (
    <>
      {!isControlled && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenInternal(true)}
          startIcon={<AddIcon />}
          size="medium"
          sx={{ fontSize: "1.2rem", width: { xs: "100%", lg: "auto" } }}
        >
          Add New User
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { maxHeight: DIALOG_HEIGHT, display: "flex" } },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                borderRadius: 2,
                bgcolor: "#2f3b4a",
                boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonAddIcon
                sx={{ fontSize: { xs: 18, sm: 22 }, color: "#fff" }}
              />
            </Box>
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontSize: { xs: "1.8rem", sm: "2rem" } }}
          >
            Add New User
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mt: 0.5,
              fontSize: { xs: "1.2rem", sm: "1.3rem" },
            }}
          >
            Create a user and assign storefront, persona, and customers
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background:
              "linear-gradient(180deg, #ffffff 0%, #fafafa 60%, #ffffff 100%)",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {!isSuccess && (
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                mb: 3,
                flexShrink: 0,
                "& .MuiStepIcon-root": {
                  fontSize: { xs: "1.8rem", sm: "2rem" },
                },
                "& .MuiStepIcon-text": {
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 700,
                },
              }}
            >
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepButton
                    onClick={() => {
                      if (idx <= activeStep) setActiveStep(idx);
                    }}
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontSize: { xs: "1.1rem", sm: "1.2rem" },
                        fontWeight: 600,
                      },
                      "&:hover .MuiStepLabel-label": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}

          {!isSuccess && activeStep === 0 && (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={2}
            >
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
                error={touched.firstName && !firstName.trim()}
                helperText={
                  touched.firstName && !firstName.trim() ? "Required" : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                }}
              />

              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
                error={touched.lastName && !lastName.trim()}
                helperText={
                  touched.lastName && !lastName.trim() ? "Required" : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                }}
              />

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                error={touched.email && !isValidEmail(email.trim())}
                helperText={
                  touched.email && !isValidEmail(email.trim())
                    ? "Enter a valid email"
                    : " "
                }
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                }}
              />

              <TextField
                label="Secondary Email (optional)"
                type="email"
                value={secondaryEmail}
                onChange={(e) => setSecondaryEmail(e.target.value)}
                onBlur={() =>
                  setTouched((t) => ({ ...t, secondaryEmail: true }))
                }
                error={
                  touched.secondaryEmail &&
                  secondaryEmail.trim().length > 0 &&
                  !isValidEmail(secondaryEmail.trim())
                }
                helperText={
                  touched.secondaryEmail &&
                  secondaryEmail.trim().length > 0 &&
                  !isValidEmail(secondaryEmail.trim())
                    ? "Enter a valid email"
                    : " "
                }
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  },
                }}
              />

              <FormControl>
                <InputLabel sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }}>
                  Storefront
                </InputLabel>

                <Select
                  label="Storefront"
                  value={storefront}
                  onChange={(e) => setStorefront(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, storefront: true }))}
                  error={touched.storefront && !storefront}
                  sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }}
                >
                  {defaultStorefronts.map((s) => (
                    <MenuItem
                      key={s.value}
                      value={s.value}
                      style={{
                        fontSize: "1.2rem",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>

                {touched.storefront && !storefront ? (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, fontSize: { xs: "1.1rem", md: "1.3rem" } }}
                  >
                    Required
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, visibility: "hidden" }}
                  >
                    helper
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}

          {!isSuccess && activeStep === 1 && (
            <Box display="grid" gridTemplateColumns={{ xs: "1fr" }} gap={2}>
              <FormControl>
                <InputLabel sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }}>
                  Persona
                </InputLabel>

                <Select
                  label="Persona"
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, persona: true }))}
                  error={touched.persona && !persona}
                  sx={{ fontSize: { xs: "1.1rem", md: "1.3rem" } }}
                >
                  {personaOptions.map((p) => (
                    <MenuItem
                      key={p}
                      value={p}
                      style={{
                        fontSize: "1.2rem",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {p}
                    </MenuItem>
                  ))}
                </Select>

                {touched.persona && !persona ? (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, fontSize: { xs: "1.1rem", md: "1.3rem" } }}
                  >
                    Required
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, visibility: "hidden" }}
                  >
                    helper
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}

          {!isSuccess && activeStep === 2 && (
            <Box>
              <Autocomplete
                multiple
                options={customerOptions}
                loading={loadingCustomers}
                value={customers}
                onChange={(_, newValue) => setCustomers(newValue)}
                getOptionLabel={(option) =>
                  `${option.externalId || option.id || ""} - ${
                    option.name || ""
                  }`
                }
                isOptionEqualToValue={(option, value) =>
                  (option.id ?? option.externalId) ===
                  (value.id ?? value.externalId)
                }
                filterSelectedOptions
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      fontSize: "1.2rem",
                      paddingTop: 10,
                      paddingBottom: 10,
                    }}
                  >
                    {option.externalId || option.id || ""} - {option.name || ""}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Customers"
                    placeholder={
                      loadingCustomers
                        ? "Loading customers..."
                        : "Search and select customers"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "1.2rem", sm: " 1.3rem" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "1.2rem", sm: " 1.3rem" },
                      },
                      "& .MuiChip-root": {
                        fontSize: "1.25rem",
                      },
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCustomers ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}

                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />

              {customers.length === 0 ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    mt: 1,
                    display: "block",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                  }}
                >
                  Select at least one customer
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block", visibility: "hidden" }}
                >
                  helper
                </Typography>
              )}
            </Box>
          )}

          {isSuccess && (
            <Box textAlign="center" sx={{ mt: 4 }}>
              <CheckCircleOutlineIcon
                color="success"
                sx={{ fontSize: 48, mb: 1 }}
              />

              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  fontWeight: 700,
                  fontSize: { xs: "1.6rem", sm: "1.8rem" },
                }}
              >
                User created successfully
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }}
              >
                {createdUser?.firstName} {createdUser?.lastName} •{" "}
                {createdUser?.email}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            "& .MuiButton-root": {
              ml: { xs: 0, sm: 1 },
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            color="inherit"
            size="medium"
            sx={{
              fontSize: { xs: "1.2rem", sm: "1.3rem" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {isSuccess ? "Close" : "Cancel"}
          </Button>

          {!isSuccess && activeStep === 0 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!detailsValid}
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Next
            </Button>
          )}

          {!isSuccess && activeStep === 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!permissionsValid}
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Next
            </Button>
          )}

          {!isSuccess && activeStep === 2 && (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!canFinish}
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Create User
            </Button>
          )}

          {isSuccess && (
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
              }}
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              View Profile
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddUserDialog;
